import React, { useState, Suspense, lazy } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import { IconPlus, IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';

// Layouts & Shared Components
import AdminLayout from '../../../layouts/AdminLayout';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import ErrorBoundary from '../../../shared/components/errors/ErrorBoundary';
import SearchComponent from '../../pms-agent/components/SearchComponent';
import RequireAuth from '../../../auth/middleware/RequireAuth';

// Hooks
import useIndexRegions from '../../../services/region/hooks/useIndexRegions';
import useDeleteRegion from '../../../services/region/hooks/useDeleteRegion';

// Types
import { RegionType } from '../../../services/region/response/RegionResponse';

// Lazy loading dialogs for optimized bundle separation
const CreateRegionDialog = lazy(() => import('../components/CreateRegionDialog'));
const EditRegionDialog = lazy(() => import('../components/EditRegionDialog'));

const RegionPage: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [editingRegionId, setEditingRegionId] = useState<string | null>(null);

    // Fetch regions using custom query hook
    const { data: regionsData, isSuccess, isError, isLoading } = useIndexRegions({
        search: searchQuery,
        page: page,
        limit: 10,
        orderBy: 'created_at',
        sort: 'desc',
    });

    const queryClient = useQueryClient();
    const { mutate: deleteRegion, isPending: isDeleting } = useDeleteRegion();

    const handleDelete = (region: RegionType) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `You will delete the region "${region.name}". This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading(`Deleting region "${region.name}"...`, { id: 'delete-region' });

                deleteRegion({ id: region.id }, {
                    onSuccess: async () => {
                        toast.success('Region deleted successfully', { id: 'delete-region' });
                        await queryClient.invalidateQueries({ queryKey: ['regions'] });
                    },
                    onError: (error: any) => {
                        toast.error(error.message || 'Failed to delete region', { id: 'delete-region' });
                    }
                });
            }
        });
    };

    const handleOpenUpdate = (id: string) => setEditingRegionId(id);
    const handleCloseUpdate = () => setEditingRegionId(null);
    const handleOpenCreate = () => setShowCreateDialog(true);
    const handleCloseCreate = () => setShowCreateDialog(false);

    return (
        <AdminLayout>
            <ErrorBoundary fallback={
                <div className="text-center p-5">
                    <h2>An Error Occurred</h2>
                    <p>Could not display the regions page.</p>
                </div>
            }>
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h3 className="mb-0">Regions</h3>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_view']} asComponent>
                                <button className="btn btn-primary d-flex align-items-center gap-1" onClick={handleOpenCreate}>
                                    <IconPlus size={18} />
                                    <span>Create Region</span>
                                </button>
                            </RequireAuth>
                        </div>

                        <div className="row g-3 align-items-center mb-4">
                            <div className="col-12 col-md-4">
                                <SearchComponent queryKey={searchQuery} onSearch={(query) => {
                                    setSearchQuery(query);
                                    setPage(1); // Reset page to 1 on search
                                }} />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Slug</th>
                                        <th>Status</th>
                                        <th>Created At</th>
                                        <th className="text-end" style={{ width: '100px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && (
                                        <tr>
                                            <td colSpan={5} className="text-center py-4">
                                                <div className="spinner-border text-primary spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                Loading regions...
                                            </td>
                                        </tr>
                                    )}
                                    {isError && (
                                        <tr>
                                            <td colSpan={5} className="text-center text-danger py-4">
                                                Failed to load data. Please try again later.
                                            </td>
                                        </tr>
                                    )}
                                    {isSuccess && regionsData.data.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-4">
                                                No regions found.
                                            </td>
                                        </tr>
                                    )}
                                    {isSuccess && regionsData.data.map((region) => (
                                        <tr key={region.id}>
                                            <td><strong>{region.name}</strong></td>
                                            <td><code>{region.slug || 'N/A'}</code></td>
                                            <td>
                                                <span className={`badge ${region.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {region.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                {new Date(region.created_at).toLocaleDateString('id-ID', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="text-end">
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle as="button" className="btn btn-icon btn-link text-secondary p-0 shadow-none no-caret border-0">
                                                        <IconDotsVertical size={20} />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                        <Dropdown.Item onClick={() => handleOpenUpdate(region.id)} className="d-flex align-items-center gap-2">
                                                            <IconEdit size={16} />
                                                            <span>Edit</span>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleDelete(region)} className="d-flex align-items-center gap-2 text-danger" disabled={isDeleting}>
                                                            <IconTrash size={16} />
                                                            <span>Delete</span>
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {isSuccess && regionsData.pagination && regionsData.data.length > 0 && (
                            <PaginationComponent
                                totalPages={regionsData.pagination.last_page}
                                currentPage={regionsData.pagination.current_page}
                                onPageChange={setPage}
                            />
                        )}
                    </div>
                </div>
            </ErrorBoundary>

            <Suspense fallback={<div className="offcanvas-backdrop fade show"></div>}>
                {showCreateDialog && (
                    <CreateRegionDialog show={showCreateDialog} onClose={handleCloseCreate} />
                )}
                {editingRegionId && (
                    <EditRegionDialog
                        regionId={editingRegionId}
                        show={!!editingRegionId}
                        onClose={handleCloseUpdate}
                    />
                )}
            </Suspense>
        </AdminLayout>
    );
};

export default RegionPage;
